import React, { useState, useContext, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Header, Button, Grid, Icon } from "semantic-ui-react";
import { ReactSVG } from "react-svg";
import Swal from 'sweetalert2';
import { UserDataViewModel } from '../../types/UserDTO';
import DataGrid, { Column } from '../Common/SharedComponents/DataGrid';
import { DynamicSearchResponse, FilterData } from '../../types/filterDataGridDTO';
import DocumentAdd from "../../assets/images/document-add.svg";
import UserInfo from '../Common/SharedComponents/UserInfo';
import AdminSidebar from '../Common/SharedComponents/AdminSidebar';
import AppContext, { token_config } from '../../AppContext';
import axiosInstance from '../../helpers/axiosInstance';
import SwitchTheme from '../Common/SharedComponents/SwitchTheme';

const Users: React.FC = () => {
  const navigate = useNavigate();
  const { userInfo } = useContext(AppContext);

  useEffect(() => {
    if (userInfo !== undefined) {
      const privileges: string[] = userInfo ? userInfo.Privileges.split(',') : [];
      if (!privileges.includes(token_config.MANAGE_USERS)) {
        navigate('/');
      }
    }
  }, [userInfo, navigate]);

  const [responseDataModel, setResponseDataModel] = useState<DynamicSearchResponse<UserDataViewModel> | null>(null);
  const [filters, setFilters] = useState<Record<string, FilterData> | null>(null);

  const columns: Column<UserDataViewModel>[] = [
    { field: 'name', header: "Name", sortable: true, headerStyle: { textAlign: 'left', paddingLeft: 10, width: '30%' }, style: { textAlign: 'left', fontSize: '16px' }, accessor: (row: UserDataViewModel) => row.name ? <>{row.status === 'Pending' ? <>{row.name} * <Icon name='trash' style={{ marginLeft: '0.1rem', cursor: 'pointer', color: '#e15247' }} onClick={() => deleteUser(row)} /></> : <Link to={`/user/${row.userId}`}>{row.name}</Link>}{row.isLocked && <Icon name='lock' style={{ marginLeft: '0.5rem', cursor: 'pointer' }} onClick={() => unlockUser(row.userId)} />}</> : null },
    { field: 'email', header: 'Email', sortable: true, className: "wrap-text", headerStyle: { width: '30%' }, accessor: (row: UserDataViewModel) => row.email ? row.email : null },
    { field: 'role', header: 'Role', sortable: true, className: "wrap-text", headerStyle: { width: '10%' }, accessor: (row: UserDataViewModel) => row.role ? row.role : null },
    { field: 'office', header: 'Office', sortable: true, className: "wrap-text", headerStyle: { width: '10%' }, accessor: (row: UserDataViewModel) => row.office ? row.office : null },
    { field: 'status', header: 'Status', sortable: true, className: "wrap-text", headerStyle: { width: '10%', textAlign: 'center' }, accessor: (row: UserDataViewModel) => row.status ? row.status : null },
  ];

  useEffect(() => {
    document.title = 'Users - Adjure';
  }, []);

  const unlockUser = (userId: number) => {
    Swal.fire({
      title: 'Are you sure you want to unlock this user?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'Cancel',
    }).then((result: any) => {
      if (result.isConfirmed) {
        try {
          axiosInstance.post(`/AdminSetting/LockUnlockUser/${userId}`, {}).then(({ data, status }) => {
            if (status === 200) {
              window.location.reload();
            }
          })
            .catch((error: any) => {
              console.log(error);
            });
        } catch (error: any) {
          console.log(error);
        }
      }
    });
  }

  const deleteUser = (row: any) => {
    Swal.fire({
      title: 'Are you sure you want to delete this user?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
    }).then((result: any) => {
      if (result.isConfirmed) {
        try {
          axiosInstance.post(`/AdminSetting/DeleteUserRegistration/`, row).then(({ data, status }) => {
            if (status === 200) {
              window.location.reload();
            }
          })
            .catch((error: any) => {
              console.log(error);
            });
        } catch (error: any) {
          console.log(error);
        }
      }
    });
  }

  return (
    <section className="section" style={{
      minHeight: '100vh'
    }}>
      <div className="section__head">
        <div className="section__head-content">
          <Header as="h1">Users</Header>
        </div>
        <div className="section__actions">
          <UserInfo />
          <Button color="violet" onClick={() => navigate('/user')}>
            <Button.Content visible style={{ whiteSpace: 'nowrap' }}>
              Create User
            </Button.Content>
            <Button.Content visible>
              <ReactSVG src={DocumentAdd} />
            </Button.Content>
          </Button>
          <SwitchTheme />
        </div>
      </div>
      <div className="section__body">
        <Grid padded>
          <Grid.Row>
            <Grid.Column mobile={16} tablet={3} computer={3}>
              <AdminSidebar />
            </Grid.Column>
            <Grid.Column mobile={16} tablet={13} computer={13}>
              <DataGrid<UserDataViewModel>
                apiUrl={`/adminsetting/UserDataGrid`}
                initialViewModelURL={`/adminsetting/GetUserDataGrid`}
                columns={columns}
                rowsPerPage={10}
                defaultSortByField={'name'}
                defaultSortOrderDesc={false}
                rowKey={(row: UserDataViewModel) => row.email}
                noDataMessage={'No user(s) to display'}
                searchPlaceholder='Search by name or email'
                filters={filters}
                setFilters={setFilters}
                responseDataModel={responseDataModel}
                setResponseDataModel={setResponseDataModel}
              />
              <div style={{ position: 'relative', bottom: '2rem', fontSize: '1.4rem' }}>* - Editing this record is not possible until the login is created. If any information is incorrect, please delete the user and create a new one.</div>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </div>
    </section>
  );
}

export default Users;